'use client'

import { useMemo } from 'react'

import type { Logger, LoggerContext } from '@/observability'

import { useClientEnv } from './use-client-env'

/**
 * Hook to create a child logger with the provided context.
 *
 * Returns a non-null `Logger` rooted on `useClientEnv().logger`. During
 * SSR the underlying logger is the SSR sentinel —
 * calling a terminal method on it reports SSR misuse via
 * `reportSSRMisuse`.
 *
 * @throws Error if used outside ClientEnvProvider
 *
 * @example
 * ```typescript
 * import { useLogger } from '@/hooks/use-logger'
 *
 * function CheckoutButton() {
 *   const logger = useLogger({ module: 'checkout-button' })
 *
 *   const handleClick = () => {
 *     logger.info('Checkout initiated', { cartTotal: 99.99 })
 *   }
 *
 *   return <button onClick={handleClick}>Checkout</button>
 * }
 * ```
 */
export function useLogger(context: LoggerContext): Logger {
    const { logger } = useClientEnv()
    // `getLogger()` builds a fresh child logger on every call, so memoize to keep the
    // returned reference stable across renders. An unstable identity would retrigger
    // any consumer effect/callback that lists the logger in its dependency array
    // (e.g. a re-render loop). Keyed on the serialized context since callers usually
    // pass a fresh object literal each render.
    const contextKey = JSON.stringify(context)
    return useMemo(
        () => logger.getLogger(context),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- context tracked via contextKey
        [logger, contextKey],
    )
}
